import { useMemo } from "react";
import type { AgentResponse } from "../../admin/agents/agentsApi";
import dayjs, { DATE_FORMAT } from "../../dayjs";
import type { CustomerContactPerson } from "../customersApi";
import type { CallRecordingResponse } from "./callRecordingsApi";
import type { CallRecordingRow } from "./CallRecordingsList";
import { formatCallDuration } from "./duration";
import { normalizePhoneNumber } from "./phoneNumber";

export function useCallRecordingRows(
  recordings: CallRecordingResponse[],
  contacts: CustomerContactPerson[],
  agents: AgentResponse[],
): CallRecordingRow[] {
  const contactNameByPhone = useMemo(() => {
    const map = new Map<string, string>();
    for (const contact of contacts) {
      if (contact.phone && contact.name) {
        map.set(normalizePhoneNumber(contact.phone), contact.name);
      }
    }
    return map;
  }, [contacts]);

  const agentNameByPhone = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents) {
      if (agent.contact.phone) {
        map.set(normalizePhoneNumber(agent.contact.phone), agent.name);
      }
    }
    return map;
  }, [agents]);

  return useMemo(
    () =>
      recordings.map((recording) => ({
        recId: recording.recId,
        contactName: contactNameByPhone.get(normalizePhoneNumber(recording.otherNumber)) ?? recording.otherNumber,
        agentName: agentNameByPhone.get(normalizePhoneNumber(recording.recordingNumber)) ?? recording.recordingNumber,
        date: dayjs(recording.startTime).format(DATE_FORMAT),
        startTime: recording.startTime,
        duration: formatCallDuration(recording.length),
        isPlayable:
          recording.status === "RECORDED" &&
          recording.fileCount > 0 &&
          !recording.recordingRemoved &&
          !recording.recordingFailed,
      })),
    [recordings, contactNameByPhone, agentNameByPhone],
  );
}
